/**
 * NativeSubscribeButton — mobile-only RevenueCat purchase + restore surface.
 *
 * Rules of engagement:
 *   • Renders nothing on the web build — Stripe checkout continues to
 *     handle app.futureself.es purchases untouched.
 *   • Only appears when Capacitor is native AND the user is NOT already
 *     premium via Stripe (server-computed `subscription_source`). This
 *     is the safest guard against double-billing.
 *   • Restore Purchases is ALWAYS shown on native (App Store 3.1.1).
 *   • After a purchase/restore we call `checkAuth()` — RevenueCat's
 *     webhook has already flipped is_premium on the backend, so the
 *     next /auth/me reflects it.
 */

import React, { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { Sparkles, RotateCcw, Loader2 } from 'lucide-react';
import { Button } from './ui/button';
import { isNativePlatform } from '../lib/native';
import { hapticLight, hapticSuccess } from '../lib/nativeFeel';
import { useAuth } from '../context/AuthContext';
import {
  configureRevenueCat,
  getOfferings,
  purchasePackage,
  restorePurchases,
} from '../lib/revenuecat';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// The RevenueCat webhook lands a few seconds after the store confirms; poll /auth/me for ~10s.
const waitForServerPremium = async (attempts = 6, delayMs = 2000) => {
  for (let i = 0; i < attempts; i++) {
    try {
      const res = await fetch(`${API_URL}/api/auth/me`, { credentials: 'include' });
      if (res.ok && (await res.json())?.is_premium) return true;
    } catch { /* retry */ }
    if (i < attempts - 1) await new Promise((r) => setTimeout(r, delayMs));
  }
  return false;
};

const NativeSubscribeButton = ({
  isPremium,
  subscriptionSource,
  onRefresh,
}) => {
  const { user, checkAuth } = useAuth();
  const [pkg, setPkg] = useState(null);
  const [busy, setBusy] = useState(null); // 'buy' | 'restore' | null
  const [ready, setReady] = useState(false);

  // Initialise RevenueCat + load offerings once per mount on native.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (!isNativePlatform() || !user?.user_id) return;
      try {
        await configureRevenueCat(user);
        const offering = await getOfferings();
        if (cancelled) return;
        // Grab the first available package — apps typically expose one
        // monthly SKU. If offerings expand later this is where paywall
        // logic would go.
        const first = offering?.availablePackages?.[0] || null;
        setPkg(first);
      } catch (e) {
        // Non-fatal: purchase button will stay disabled with a friendly
        // fallback tooltip via `ready` remaining false.
      } finally {
        if (!cancelled) setReady(true);
      }
    })();
    return () => { cancelled = true; };
  }, [user]);

  // Web: no UI at all. Existing Stripe UI in SubscriptionSection stays.
  if (!isNativePlatform()) return null;

  const managedByStripe = subscriptionSource === 'stripe';
  const managedByApple = subscriptionSource === 'apple_iap';
  const managedByGoogle = subscriptionSource === 'google_play';

  const handlePurchase = async () => {
    if (!pkg || busy) return;
    setBusy('buy');
    hapticLight();
    try {
      const result = await purchasePackage(pkg);
      const active = await waitForServerPremium();
      await checkAuth();
      if (onRefresh) await onRefresh();
      if (result?.isPremium && active) {
        hapticSuccess();
        toast.success('Welcome to Premium — enjoy your journey.');
      } else {
        toast('Purchase completed. Premium may take a moment to appear — reopen the app if it does not update.');
      }
    } catch (e) {
      const msg = e?.message || '';
      // Silent on user-cancelled; friendly on the rest.
      if (/cancel|1|user_cancel|USER_CANCELLED/i.test(msg)) {
        // no-op
      } else if (/network|offline|internet/i.test(msg)) {
        toast.error("We couldn't reach the store. Check your connection and try again.");
      } else {
        toast.error("We couldn't complete your purchase. Please try again.");
      }
    } finally {
      setBusy(null);
    }
  };

  const handleRestore = async () => {
    if (busy) return;
    setBusy('restore');
    hapticLight();
    try {
      const result = await restorePurchases();
      if (result?.isPremium) await waitForServerPremium();
      await checkAuth();
      if (onRefresh) await onRefresh();
      if (result?.isPremium) {
        hapticSuccess();
        toast.success('Your Premium subscription has been restored.');
      } else {
        toast('No prior purchases found for this Apple ID / Google account.');
      }
    } catch (e) {
      toast.error("We couldn't restore purchases. Please try again.");
    } finally {
      setBusy(null);
    }
  };

  return (
    <div className="space-y-3" data-testid="native-subscribe-block">
      {/* Existing Stripe subscribers on native — explain the situation and
          direct them to the web to manage. No Subscribe CTA (double-billing
          risk). Restore Purchases still shows (they may have had a prior
          native purchase we should reattach). */}
      {isPremium && managedByStripe && (
        <div
          className="rounded-2xl bg-[#1C1C1E] border border-white/5 p-4 text-center"
          data-testid="stripe-managed-on-web-hint"
        >
          <p className="text-[#DCD3CA] font-sans text-sm mb-2">
            Your Premium subscription is managed on the web.
          </p>
          <button
            type="button"
            onClick={async () => {
              hapticLight();
              try {
                const mod = await import('@capacitor/browser').catch(() => null);
                if (mod?.Browser?.open) {
                  await mod.Browser.open({ url: 'https://app.futureself.es/settings' });
                  return;
                }
              } catch { /* fall through */ }
              window.open('https://app.futureself.es/settings', '_blank', 'noopener,noreferrer');
            }}
            className="text-[#8BAA9E] hover:text-[#A3C2B6] text-xs font-sans underline underline-offset-4 decoration-[#8BAA9E]/40"
            data-testid="manage-on-web-btn"
          >
            Manage on web ↗
          </button>
        </div>
      )}

      {/* Purchase button: only when not already premium, and definitely not
          for a Stripe-paid user (avoid double billing). */}
      {!isPremium && !managedByStripe && (
        <Button
          type="button"
          onClick={handlePurchase}
          disabled={!ready || !pkg || busy !== null}
          className="w-full rounded-full bg-gradient-to-r from-[#C6A75E] to-[#D4B56A] text-[#1C1C1E] font-sans font-medium h-12"
          data-testid="native-subscribe-btn"
        >
          {busy === 'buy' ? (
            <span className="flex items-center gap-2">
              <Loader2 className="w-4 h-4 animate-spin" /> Processing…
            </span>
          ) : (
            <span className="flex items-center gap-2">
              <Sparkles className="w-4 h-4" strokeWidth={1.75} />
              Subscribe to Premium
            </span>
          )}
        </Button>
      )}
      {!isPremium && !managedByStripe && pkg?.product?.priceString && (
        <p
          className="text-[#B5B1AB] text-xs font-sans text-center"
          data-testid="native-subscribe-price"
        >
          {pkg.product.priceString} per month. Renews automatically, cancel anytime.
        </p>
      )}

      {/* Restore Purchases is required by App Store Guideline 3.1.1 for
          any auto-renewable subscription — always visible on native. */}
      <Button
        type="button"
        variant="outline"
        onClick={handleRestore}
        disabled={busy !== null}
        className="w-full rounded-full border-[#B8AFA6]/30 text-[#DCD3CA] hover:bg-white/5 font-sans h-11"
        data-testid="restore-purchases-btn"
      >
        {busy === 'restore' ? (
          <span className="flex items-center gap-2">
            <Loader2 className="w-4 h-4 animate-spin" /> Restoring…
          </span>
        ) : (
          <span className="flex items-center gap-2">
            <RotateCcw className="w-4 h-4" strokeWidth={1.75} />
            Restore Purchases
          </span>
        )}
      </Button>

      {isPremium && managedByApple && (
        <Button
          type="button"
          variant="outline"
          onClick={async () => {
            hapticLight();
            const url = 'https://apps.apple.com/account/subscriptions';
            try {
              const mod = await import('@capacitor/browser').catch(() => null);
              if (mod?.Browser?.open) {
                await mod.Browser.open({ url });
                return;
              }
            } catch { /* fall through */ }
            window.open(url, '_blank', 'noopener,noreferrer');
          }}
          className="w-full rounded-full border-[#C6A75E]/40 text-[#C6A75E] hover:bg-white/5 font-sans h-11"
          data-testid="manage-app-store-subscription-btn"
        >
          Manage Subscription in App Store
        </Button>
      )}

      {isPremium && managedByGoogle && (
        <p className="text-[#7A7670] text-xs font-sans text-center leading-relaxed">
          Manage or cancel your subscription in Google Play.
        </p>
      )}
    </div>
  );
};

export default NativeSubscribeButton;
